import { z } from 'zod';

/**
 * The operator's CRM in /platform (ADR-0151, BR-255): a contact person, notes and tasks per
 * merchant, the merchant timeline, operator tasks and the prospects pipeline (shops that have
 * not registered yet). Operator data only; merchants never see it.
 */

export const PROSPECT_STAGES = ['new', 'contacted', 'demo', 'trial', 'won', 'lost'] as const;
export type ProspectStage = (typeof PROSPECT_STAGES)[number];
export const PROSPECT_TYPES = ['restaurant', 'retail', 'cafe', 'other'] as const;
export type ProspectType = (typeof PROSPECT_TYPES)[number];
export const PROSPECT_SOURCES = [
  'visit',
  'call',
  'referral',
  'social',
  'exhibition',
  'website',
  'other',
] as const;
export type ProspectSource = (typeof PROSPECT_SOURCES)[number];

export const TIMELINE_KINDS = [
  'registered',
  'first_product',
  'first_sale',
  'device_trial',
  'subscription',
  'status',
  'note',
  'task',
  'task_done',
  'prospect_linked',
] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

/** Free text that may be left empty: '' and null both mean "none". */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? null : v));
/** Phone / WhatsApp number as typed (digits, spaces, + - ( )). */
const phone = z
  .string()
  .trim()
  .max(30)
  .regex(/^[0-9+\-() ]*$/)
  .nullish()
  .transform((v) => (v == null || v === '' ? null : v));
const day = z.iso.date();

export const crmOperatorSchema = z.object({ id: z.string(), fullName: z.string() });
export type CrmOperator = z.infer<typeof crmOperatorSchema>;
export const crmOperatorListSchema = z.object({ operators: z.array(crmOperatorSchema) });

// ------------------------------------------------------------------ contact
export const crmContactSchema = z.object({
  name: z.string().nullable(),
  phone: z.string().nullable(),
  whatsapp: z.string().nullable(),
  notes: z.string().nullable(),
  version: z.number().int(),
  updatedAt: z.string(),
  updatedByName: z.string().nullable(),
});
export type CrmContact = z.infer<typeof crmContactSchema>;

/** `version` 0 creates the contact; otherwise the current version (409 when stale). */
export const saveCrmContactSchema = z.object({
  name: optionalText(120),
  phone,
  whatsapp: phone,
  notes: optionalText(1000),
  version: z.number().int().min(0),
});
export type SaveCrmContact = z.input<typeof saveCrmContactSchema>;

// ------------------------------------------------------------------ notes
export const crmNoteSchema = z.object({
  id: z.string(),
  body: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  createdAt: z.string(),
  hiddenAt: z.string().nullable(),
  hiddenByName: z.string().nullable(),
  hiddenReason: z.string().nullable(),
});
export type CrmNote = z.infer<typeof crmNoteSchema>;
export const addCrmNoteSchema = z.object({ body: z.string().trim().min(1).max(2000) });
export type AddCrmNote = z.input<typeof addCrmNoteSchema>;
export const hideCrmNoteSchema = z.object({ reason: z.string().trim().min(3).max(300) });
export type HideCrmNote = z.input<typeof hideCrmNoteSchema>;

// ------------------------------------------------------------------ tasks
export const crmTaskSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  merchantNameEn: z.string().nullable(),
  merchantNameAr: z.string().nullable(),
  title: z.string(),
  dueOn: z.string(),
  assigneeId: z.string(),
  assigneeName: z.string(),
  createdByName: z.string(),
  doneAt: z.string().nullable(),
  doneByName: z.string().nullable(),
  /** Open and due before today (operator time zone, Asia/Kuwait). */
  overdue: z.boolean(),
  /** Open and due today. */
  dueToday: z.boolean(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type CrmTask = z.infer<typeof crmTaskSchema>;
export const crmTaskListSchema = z.object({ tasks: z.array(crmTaskSchema) });

export const createCrmTaskSchema = z.object({
  title: z.string().trim().min(1).max(200),
  dueOn: day,
  assigneeId: z.uuid(),
});
export type CreateCrmTask = z.input<typeof createCrmTaskSchema>;
export const updateCrmTaskSchema = createCrmTaskSchema.extend({
  done: z.boolean(),
  version: z.number().int().min(1),
});
export type UpdateCrmTask = z.input<typeof updateCrmTaskSchema>;
export const crmTaskQuerySchema = z.object({
  scope: z.enum(['mine', 'all']).default('mine'),
  status: z.enum(['open', 'done']).default('open'),
});
export type CrmTaskQuery = z.input<typeof crmTaskQuerySchema>;

// ------------------------------------------------------------------ merchant CRM + timeline
export const crmMerchantSchema = z.object({
  contact: crmContactSchema.nullable(),
  notes: z.array(crmNoteSchema),
  tasks: z.array(crmTaskSchema),
  prospect: z
    .object({ id: z.string(), name: z.string(), stage: z.enum(PROSPECT_STAGES) })
    .nullable(),
});
export type CrmMerchant = z.infer<typeof crmMerchantSchema>;

export const timelineEventSchema = z.object({
  kind: z.enum(TIMELINE_KINDS),
  occurredAt: z.string(),
  detail: z.string().nullable(),
  actorName: z.string().nullable(),
});
export type TimelineEvent = z.infer<typeof timelineEventSchema>;
export const timelineSchema = z.object({ events: z.array(timelineEventSchema) });

// ------------------------------------------------------------------ prospects
export const prospectSchema = z.object({
  id: z.string(),
  name: z.string(),
  businessType: z.enum(PROSPECT_TYPES),
  area: z.string().nullable(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  source: z.enum(PROSPECT_SOURCES),
  stage: z.enum(PROSPECT_STAGES),
  lostReason: z.string().nullable(),
  nextStepOn: z.string().nullable(),
  nextStep: z.string().nullable(),
  notes: z.string().nullable(),
  ownerId: z.string(),
  ownerName: z.string(),
  merchantId: z.string().nullable(),
  merchantNameEn: z.string().nullable(),
  merchantNameAr: z.string().nullable(),
  /** Open (not won / lost) with a next step before today. */
  overdue: z.boolean(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Prospect = z.infer<typeof prospectSchema>;
export const prospectListSchema = z.object({
  prospects: z.array(prospectSchema),
  counts: z.record(z.enum(PROSPECT_STAGES), z.number().int()),
});
export type ProspectList = z.infer<typeof prospectListSchema>;

export const saveProspectSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    businessType: z.enum(PROSPECT_TYPES),
    area: optionalText(120),
    contactName: optionalText(120),
    phone,
    source: z.enum(PROSPECT_SOURCES),
    stage: z.enum(PROSPECT_STAGES),
    lostReason: optionalText(300),
    nextStepOn: day.nullish().transform((v) => v ?? null),
    nextStep: optionalText(200),
    notes: optionalText(1000),
    ownerId: z.uuid(),
    /** 0 for a new prospect. */
    version: z.number().int().min(0),
  })
  .refine((v) => (v.stage === 'lost') === (v.lostReason !== null), {
    path: ['lostReason'],
    message: 'A lost prospect needs a reason; only a lost one has it.',
  });
export type SaveProspect = z.input<typeof saveProspectSchema>;
export const prospectQuerySchema = z.object({
  stage: z.enum(PROSPECT_STAGES).optional(),
  q: z.string().trim().max(100).optional(),
  mine: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type ProspectQuery = z.input<typeof prospectQuerySchema>;
/** Links a prospect to the merchant it became; the prospect is then won. */
export const linkProspectSchema = z.object({
  merchantId: z.uuid(),
  version: z.number().int().min(1),
});
export type LinkProspect = z.input<typeof linkProspectSchema>;
